/// <reference path="../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />
import { ITranscriptionManager, ITranscription } from '../interfaces/ITranscriptionManager'
import { LogEvents } from '../../utils/LogEvents';
import { DurationMeasureType } from '../interfaces/ILogEvent'
import { AddTranscriptionToDocumentKeys } from '../office-js/telemetry/OfficeJSKeys';
import { ITranscriptionSourceFile } from '../interfaces/ITranscriptionSourceFileProvider';
import { IAddTranscriptionToCanvas, AddToDocumentOptions, GetMergedSpeakerTranscription} from '../interfaces/IAddTranscriptionToCanvas';
import { GetSpeakerName } from '../../frontend/common/actions/TranscribingActions';
import { ErrorStringFromException, isNullOrUndefined } from '../../utils/utils';
import { FormatTime } from '../../frontend/utils';
import { HostAppUtil } from '../office-js/HostAppUtil';
import { AddToDocumentResult, HOST_PLATFORM_WIN32, MAX_WAIT_TIME_FOR_ADD_TO_DOCUMENT_REQUEST } from '../../AppConstants';

// Size kept 1000 characters, 1000 found to be optimum value as
// larger chunks were increasing overall processing time of insertion
let INSERT_TEXT_CHUNK_SIZE = 1000;

type formattedTranscriptText = { textToInsert: string, characterCount: number };
export class AddTranscriptionToWordDocument implements IAddTranscriptionToCanvas {

  async AddTranscriptionToDocument(transcription: ITranscription, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentOptions, callback?: Function) {
    if (HostAppUtil.GetHostAppPlatform() == HOST_PLATFORM_WIN32) {
      this.AddTranscriptionToWordWin32Document(transcription, sourceFile, addOption, callback);
    } else {
      this.AddTranscriptionToWordWebDocument(transcription, sourceFile, addOption, callback);
    }
  }

  private async AddTranscriptionToWordWebDocument(transcription: ITranscription, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentOptions, callback?: Function) {
    LogEvents.OfficeJS.Start();
    LogEvents.OfficeJS.BeginDurationMeasure(DurationMeasureType.Normal, AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentDuration);

    try {
      let characterCount = 0;

      let addTranscriptionTimeOut:any | null = null;
      let failedChunkIndexInfo = "";
      await Word.run(async (context) => {

          // Time out will trigger callback in case no response is received against AddToDocument request from client side in 2 mins.
          if (!isNullOrUndefined(callback)) {
            addTranscriptionTimeOut = setTimeout(() => {
              callback!(AddToDocumentResult.TIMED_OUT);
              callback = undefined;
            }, MAX_WAIT_TIME_FOR_ADD_TO_DOCUMENT_REQUEST);
          }

          const currentSelectionRange: Word.Range = context.document.getSelection();
          let transcriptSectionStartRange = currentSelectionRange;

          if (sourceFile != null) {
            // Insert audio file section
            const audioFileHeadingRange: Word.Range = currentSelectionRange.insertText(TranscriptionStrings.InDocumentTranscriptAudioFileHeading, Word.InsertLocation.end);
            characterCount += TranscriptionStrings.InDocumentTranscriptAudioFileHeading.length;
            audioFileHeadingRange.styleBuiltIn = 'Heading1';

            const breakRangeAfterAudioFileHeading = audioFileHeadingRange.insertText('\n', Word.InsertLocation.end);
            breakRangeAfterAudioFileHeading.styleBuiltIn = 'Normal';
            characterCount += 1;

            const hyperlinkRange = breakRangeAfterAudioFileHeading.insertText(sourceFile.fileName, Word.InsertLocation.end);
            characterCount += sourceFile.fileName.length;
            hyperlinkRange.hyperlink = sourceFile.permanentAudioUrl;
            const breakRangeAfterHyperlink = hyperlinkRange.insertText('\n', Word.InsertLocation.after);
            transcriptSectionStartRange = breakRangeAfterHyperlink;
          }

          // Insert transcript section
          const headingRange: Word.Range = transcriptSectionStartRange.insertText(TranscriptionStrings.InDocumentTranscriptTranscriptHeading, Word.InsertLocation.after);
          headingRange.styleBuiltIn = 'Heading1';

          const breakRange = headingRange.insertText('\n', Word.InsertLocation.end);
          breakRange.styleBuiltIn = 'Normal';
          let textToInsert: string = '';
          const formattedText = this.FormatTranscriptionText(transcription, characterCount, addOption);
          characterCount = formattedText.characterCount;
          textToInsert = formattedText.textToInsert;


          // Complete transcription is available in textToInsert.
          // Inserting transcript in chunks to avoid lag in processing.
          let chunkIndex = 0;
          let isFailureObserved = false;
          INSERT_TEXT_CHUNK_SIZE = 5000;

          while (chunkIndex * INSERT_TEXT_CHUNK_SIZE < textToInsert.length) {
            try {
              breakRange.insertText(
                textToInsert.substring(chunkIndex * INSERT_TEXT_CHUNK_SIZE,
                  Math.min((chunkIndex + 1) * INSERT_TEXT_CHUNK_SIZE, textToInsert.length)),
                  Word.InsertLocation.end);
                  await context.sync();
            } catch(e) {
              if (!isFailureObserved) {
                failedChunkIndexInfo = "AddDocument failed for chunkIndex ";
                isFailureObserved = true;
              }
              failedChunkIndexInfo += chunkIndex + " with error reason "+ ErrorStringFromException(e) + " : ";
            } finally {
              chunkIndex++;
            }
          }
          breakRange.select(Word.SelectionMode.end);

          if (!isNullOrUndefined(callback)) {
            try {
              await context.sync();
              callback!(AddToDocumentResult.SUCCESS);
            } catch (e) {
              callback!(AddToDocumentResult.FAILURE, e);
            } finally {
              if (!isNullOrUndefined(addTranscriptionTimeOut)) {
                clearTimeout(addTranscriptionTimeOut!);
              }
            }
          }

      });

      LogEvents.OfficeJS.End([{ key: AddTranscriptionToDocumentKeys.CharacterCount, value: characterCount }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithTimestamps , value: addOption.withTimestamps }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithSpeakers , value: addOption.withSpeakers }, { key: AddTranscriptionToDocumentKeys.FailedChunkIndecesInfo, value: failedChunkIndexInfo }]);
    } catch (e) {
    LogEvents.OfficeJS.End([{key: AddTranscriptionToDocumentKeys.FailureReason, value: ErrorStringFromException(e)}]);
    throw e;
    }
  }

  private async  AddTranscriptionToWordWin32Document(transcription: ITranscription, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentOptions, callback?:Function) {
    LogEvents.OfficeJS.Start();
    LogEvents.OfficeJS.BeginDurationMeasure(DurationMeasureType.Normal, AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentDuration);

    try {
      let characterCount = 0;
      let addTranscriptionTimeOut: any | null = null;

      await Word.run(async (context) => {

          // Time out will trigger callback in case no response is received against AddToDocument request from client side in 2 mins.
          if (!isNullOrUndefined(callback)) {
            addTranscriptionTimeOut = setTimeout(() => {
              callback!(AddToDocumentResult.TIMED_OUT);
              callback = undefined;
            }, MAX_WAIT_TIME_FOR_ADD_TO_DOCUMENT_REQUEST);
          }

          const currentSelectionRange: Word.Range = context.document.getSelection();
          let transcriptSectionStartRange = currentSelectionRange;

          if (sourceFile != null) {
            // Insert audio file section
            const audioFileHeadingRange: Word.Range = currentSelectionRange.insertText(TranscriptionStrings.InDocumentTranscriptAudioFileHeading + '\n', Word.InsertLocation.end);
            characterCount += TranscriptionStrings.InDocumentTranscriptAudioFileHeading.length;
            audioFileHeadingRange.styleBuiltIn = 'Heading1';

            const breakRangeAfterAudioFileHeading = audioFileHeadingRange.insertText('\n', Word.InsertLocation.after);
            breakRangeAfterAudioFileHeading.styleBuiltIn = 'Normal';
            characterCount += 1;

            const hyperlinkRange = breakRangeAfterAudioFileHeading.insertText(sourceFile.fileName, Word.InsertLocation.end);
            hyperlinkRange.styleBuiltIn = 'Normal';
            characterCount += sourceFile.fileName.length;
            hyperlinkRange.hyperlink = sourceFile.permanentAudioUrl;
            const breakRangeAfterHyperlink = hyperlinkRange.insertText('\n\n', Word.InsertLocation.after);
            transcriptSectionStartRange = breakRangeAfterHyperlink;
          }

          // Insert transcript section
          const headingRange: Word.Range = transcriptSectionStartRange.insertText(TranscriptionStrings.InDocumentTranscriptTranscriptHeading +'\n', Word.InsertLocation.end);
          headingRange.styleBuiltIn = 'Heading1';

          const breakRange = headingRange.insertText('\n', Word.InsertLocation.after);
          breakRange.styleBuiltIn = 'Normal';
          let textToInsert: string = '';
          const formattedText = this.FormatTranscriptionText(transcription, characterCount, addOption);
          characterCount = formattedText.characterCount;
          textToInsert = formattedText.textToInsert;

          // Complete transcription is available in textToInsert.
          // Inserting transcript in one go for performance optimization
          breakRange.insertText(
            textToInsert,
              Word.InsertLocation.end);
          breakRange.select(Word.SelectionMode.end);

          if (!isNullOrUndefined(callback)) {
            try {
              await context.sync();
              callback!(AddToDocumentResult.SUCCESS);
            } catch (e) {
              callback!(AddToDocumentResult.FAILURE, e);
            } finally {
              if (!isNullOrUndefined(addTranscriptionTimeOut)) {
                clearTimeout(addTranscriptionTimeOut!);
              }
            }
          }
      });
      LogEvents.OfficeJS.End([{ key: AddTranscriptionToDocumentKeys.CharacterCount, value: characterCount }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithTimestamps , value: addOption.withTimestamps }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithSpeakers , value: addOption.withSpeakers }]);
    } catch (e) {
    LogEvents.OfficeJS.End([{key: AddTranscriptionToDocumentKeys.FailureReason, value: ErrorStringFromException(e)}]);
    throw e;
    }
  }

  async  AddTranscriptionSegmentToDocument(transcriptionManager: ITranscriptionManager, segmentIndex: number) {
    LogEvents.OfficeJS.Start();
    LogEvents.OfficeJS.BeginDurationMeasure(DurationMeasureType.Normal, AddTranscriptionToDocumentKeys.AddTranscriptionSegmentToDocumentDuration);

    try {
      let characterCount = 0;

      const segment = transcriptionManager.GetTranscription().transcriptSegments[segmentIndex];
      await Word.run(async (context) => {
          const currentSelectionRange: Word.Range = context.document.getSelection();
          let segmentRange: Word.Range;

          if (HostAppUtil.GetHostAppPlatform() == HOST_PLATFORM_WIN32) {
            segmentRange = currentSelectionRange.insertText(FormatTime(segment.start) + (' ' + GetSpeakerName(segment.speakerId, transcriptionManager.GetTranscription().speakerNames)) + '\n' + segment.text + '\n\n', Word.InsertLocation.end);
            segmentRange.styleBuiltIn = 'Normal';
          } else {
            segmentRange = currentSelectionRange.insertText(FormatTime(segment.start) + (' ' + GetSpeakerName(segment.speakerId, transcriptionManager.GetTranscription().speakerNames)) + '\n' + segment.text + '\n', Word.InsertLocation.end);
          }

          if (segment.text != undefined) {
            characterCount += segment.text.length + 1;
          }
          segmentRange.select(Word.SelectionMode.end);
      });
      LogEvents.OfficeJS.End([{ key: AddTranscriptionToDocumentKeys.CharacterCount, value: characterCount }]);
    } catch (e) {
      LogEvents.OfficeJS.End([{key: AddTranscriptionToDocumentKeys.FailureReason, value: ErrorStringFromException(e)}]);
      throw e;
    }
  }

   private FormatTranscriptionText(transcription: ITranscription, characterCount : number, addOption: AddToDocumentOptions) : formattedTranscriptText {
    characterCount += TranscriptionStrings.InDocumentTranscriptTranscriptHeading.length;
    characterCount += 1;

    // Collecting complete transcription in textToInsert to later
    // insert it into word document
    // Can't use segment for loop to direct insertion as it will
    // be too many request with small string sizes and complete
    // for loop execution is not taking more than 2-3 ms.
    // Note: this is a special case for solving the OCVs feedback of same speaker merged text feedback. Kept behind feature flag.
    if (!addOption.withTimestamps) {
      return this.FormatTranscriptionTextWithSpeakerOnlyOrJustText(transcription, characterCount, addOption);
    }

    let textToInsert: string = '';
    for (const segment of transcription.transcriptSegments) {
      if (addOption.withTimestamps) {
        textToInsert +=  FormatTime(segment.start);
        if (!addOption.withSpeakers) {
          textToInsert += '\n';
        }
      }

      if (addOption.withSpeakers) {
        textToInsert +=  (segment.speakerId != null ? ' ' + GetSpeakerName(segment.speakerId, transcription.speakerNames) : '')  + '\n';
      }

      if (segment.text != undefined && segment.text != "") {
        textToInsert += segment.text + '\n';
        characterCount += segment.text.length + 1;
      }
    }
    return {textToInsert, characterCount};
  }

  private FormatTranscriptionTextWithSpeakerOnlyOrJustText(transcription: ITranscription, characterCount : number, addOption : AddToDocumentOptions): formattedTranscriptText {
    const mergedTranscription = GetMergedSpeakerTranscription(transcription);
    let textToInsert: string = '';
    for (let index = 0; index < mergedTranscription.transcriptSegments.length; index++) {
      if (addOption.withSpeakers){
        textToInsert += GetSpeakerName(mergedTranscription.transcriptSegments[index].speakerId, mergedTranscription.speakerNames) + '\n';
      }
      if (mergedTranscription.transcriptSegments[index].text != undefined && mergedTranscription.transcriptSegments[index].text != "") {
        textToInsert += mergedTranscription.transcriptSegments[index].text + '\n';
        characterCount += mergedTranscription.transcriptSegments[index].text.length + 1;
      }
    }
    return {textToInsert, characterCount};
  }
}